import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

const LocationManager = ({ warehouse, onClose, onUpdate }) => {
    const { user } = useAuth();
    const { showToast, showConfirm } = useUI();
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [parentId, setParentId] = useState(null);
    const [targetType, setTargetType] = useState('Zone');

    const [formData, setFormData] = useState({
        name: '',
        type: 'Zone',
        warehouseId: warehouse.id,
        companyId: user.companyId,
        parentId: null,
        x: 0, y: 0, z: 0,
        width: 1, height: 1, depth: 1
    });

    const fetchLocations = useCallback(async () => {
        try {
            const res = await fetch(`/api/locations?warehouseId=${warehouse.id}&companyId=${user.companyId}`);
            if (res.ok) {
                const data = await res.json();
                setLocations(data);
            }
        } catch (error) {
            showToast("Erreur lors du chargement des emplacements", "error");
        } finally {
            setLoading(false);
        }
    }, [warehouse.id, user.companyId, showToast]);

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    const toggleExpand = (id) => {
        const next = new Set(expandedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setExpandedIds(next);
    };

    const handleOpenAdd = (pId = null, type = 'Zone') => {
        setParentId(pId);
        setTargetType(type);
        setFormData({
            ...formData,
            name: '',
            type,
            parentId: pId,
            warehouseId: warehouse.id,
            companyId: user.companyId
        });
        setEditingLocation(null);
        setShowAddModal(true);
    };

    const handleOpenEdit = (loc) => {
        setEditingLocation(loc);
        setFormData({
            name: loc.name,
            type: loc.type,
            parentId: loc.parentId,
            warehouseId: loc.warehouseId,
            companyId: loc.companyId,
            x: loc.x, y: loc.y, z: loc.z,
            width: loc.width, height: loc.height, depth: loc.depth
        });
        setShowAddModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const method = editingLocation ? 'PUT' : 'POST';
            const url = editingLocation ? `/api/locations/${editingLocation.id}` : '/api/locations';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                showToast(editingLocation ? "Emplacement mis à jour" : "Emplacement créé", "success");
                setShowAddModal(false);
                fetchLocations();
                if (onUpdate) onUpdate();
            } else {
                showToast("Erreur lors de l'enregistrement", "error");
            }
        } catch (error) {
            showToast("Erreur serveur", "error");
        }
    };

    const handleDelete = async (id) => {
        showConfirm({
            title: "Supprimer l'emplacement ?",
            message: "Cela supprimera également tous les sous-emplacements associés.",
            onConfirm: async () => {
                try {
                    const res = await fetch(`/api/locations/${id}`, { method: 'DELETE' });
                    if (res.ok) {
                        showToast("Emplacement supprimé", "success");
                        fetchLocations();
                        if (onUpdate) onUpdate();
                    }
                } catch (error) {
                    showToast("Erreur lors de la suppression", "error");
                }
            }
        });
    };

    const handleDuplicate = async (id) => {
        try {
            const res = await fetch(`/api/locations/${id}/duplicate`, { method: 'POST' });
            if (res.ok) {
                showToast("Emplacement dupliqué avec tous ses sous-éléments", "success");
                fetchLocations();
                if (onUpdate) onUpdate();
            } else {
                showToast("Erreur lors de la duplication", "error");
            }
        } catch (error) {
            showToast("Erreur serveur", "error");
        }
    };


    const getNextType = (currentType) => {
        switch (currentType) {
            case 'Zone': return 'Aisle';
            case 'Aisle': return 'Element';
            case 'Element': return 'Slot';
            default: return null;
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'Zone': return 'Zone';
            case 'Aisle': return 'Allée';
            case 'Element': return 'Élément (Rack/Armoire)';
            case 'Slot': return 'Emplacement (Étagère/Niveau)';
            default: return type;
        }
    };

    const LocationItem = ({ loc, depth = 0 }) => {
        const nextType = getNextType(loc.type);
        const isExpanded = expandedIds.has(loc.id);
        const children = locations.filter(l => l.parentId === loc.id);

        return (
            <div className="select-none animate-in fade-in slide-in-from-left-2 duration-300">
                <div
                    className={`flex items-center justify-between p-4 rounded-2xl hover:bg-white dark:hover:bg-slate-800 hover:shadow-sm transition-all group ${depth === 0 ? 'bg-white/60 dark:bg-slate-800/60 shadow-sm border border-slate-100 dark:border-slate-800' : ''}`}
                    style={{ marginLeft: `${depth * 28}px` }}
                >
                    <div className="flex items-center gap-4">
                        {nextType && (
                            <button
                                onClick={() => toggleExpand(loc.id)}
                                className={`size-6 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-400 hover:text-primary transition-all duration-300 ${isExpanded ? 'rotate-90' : ''}`}
                            >
                                <span className="material-symbols-outlined text-[16px] font-black">chevron_right</span>
                            </button>
                        )}
                        {!nextType && <span className="w-6"></span>}
                        <div className={`size-10 rounded-xl flex items-center justify-center shrink-0 ${loc.type === 'Zone' ? 'bg-primary/10 text-primary' : loc.type === 'Aisle' ? 'bg-amber-50 text-amber-500' : 'bg-slate-100 text-slate-500'}`}>
                            <span className="material-symbols-outlined text-[22px]">
                                {loc.type === 'Zone' ? 'grid_view' : loc.type === 'Aisle' ? 'width' : loc.type === 'Element' ? 'inventory_2' : 'location_on'}
                            </span>
                        </div>
                        <div>
                            <p className="font-black text-slate-900 dark:text-white text-sm">{loc.name}</p>
                            <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest">{getTypeLabel(loc.type)}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                        {nextType && (
                            <button
                                onClick={() => handleOpenAdd(loc.id, nextType)}
                                className="h-8 px-4 text-[9px] font-black bg-primary/10 text-primary rounded-lg hover:bg-primary hover:text-white transition-all uppercase tracking-wider"
                            >
                                + {getTypeLabel(nextType)}
                            </button>
                        )}
                        <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-1"></div>
                        <button onClick={() => handleDuplicate(loc.id)} className="size-8 flex items-center justify-center text-slate-400 hover:bg-emerald-50 hover:text-emerald-500 rounded-lg transition-all" title="Dupliquer">
                            <span className="material-symbols-outlined text-base">content_copy</span>
                        </button>
                        <button onClick={() => handleOpenEdit(loc)} className="size-8 flex items-center justify-center text-slate-400 hover:bg-primary/10 hover:text-primary rounded-lg transition-all">
                            <span className="material-symbols-outlined text-base">edit</span>
                        </button>
                        <button onClick={() => handleDelete(loc.id)} className="size-8 flex items-center justify-center text-slate-400 hover:bg-rose-50 hover:text-rose-500 rounded-lg transition-all">
                            <span className="material-symbols-outlined text-base">delete</span>
                        </button>
                    </div>
                </div>

                {isExpanded && (
                    <div className="relative">
                        {/* Decorative Line for Hierarchy */}
                        <div className="absolute left-[13px] top-0 bottom-4 w-0.5 bg-slate-100 dark:bg-slate-800 rounded-full" style={{ marginLeft: `${depth * 28}px` }}></div>
                        <div className="space-y-1 pt-1">
                            {children.map(child => (
                                <LocationItem key={child.id} loc={child} depth={depth + 1} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-primary/20 w-full max-w-4xl flex flex-col max-h-[90vh] border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-500">

                {/* Premium Header with Gradient */}
                <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20">
                    <div className="relative z-10 flex justify-between items-center">
                        <div className="flex gap-4 items-center">
                            <div className="size-12 rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-sm border border-primary/10">
                                <span className="material-symbols-outlined text-primary text-[28px] font-bold">account_tree</span>
                            </div>
                            <div>
                                <h2 className="text-xl font-black text-slate-900 dark:text-white leading-tight">Architecture Logistique</h2>
                                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] opacity-70">
                                    {warehouse.name} • {locations.length} points de stockage
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="size-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow transition-all"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-8 space-y-6">
                    <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                            <div className="w-1 h-4 bg-primary rounded-full"></div>
                            <h3 className="font-black text-xs uppercase tracking-widest text-slate-400">Structure Hiérarchique</h3>
                        </div>
                        <button
                            onClick={() => handleOpenAdd(null, 'Zone')}
                            className="h-10 px-6 bg-primary text-white text-xs font-black rounded-xl shadow-lg shadow-primary/20 hover:bg-primary/90 transition-all flex items-center gap-2"
                        >
                            <span className="material-symbols-outlined text-sm font-black">add_circle</span>
                            <span>Nouvelle Zone</span>
                        </button>
                    </div>

                    {loading ? (
                        <div className="py-24 text-center">
                            <div className="inline-block size-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-4"></div>
                            <p className="text-slate-400 font-bold text-sm">Synchronisation des données...</p>
                        </div>
                    ) : locations.length === 0 ? (
                        <div className="py-24 text-center bg-slate-50/50 dark:bg-slate-800/30 rounded-[2rem] border-2 border-dashed border-slate-100 dark:border-slate-800 animate-in fade-in duration-700">
                            <div className="size-20 rounded-[2rem] bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-6 transform -rotate-12">
                                <span className="material-symbols-outlined text-4xl text-slate-300">account_tree</span>
                            </div>
                            <p className="text-slate-900 dark:text-white font-black text-lg">Entrepôt Vierge</p>
                            <p className="text-sm text-slate-500 mt-2 max-w-xs mx-auto mb-8 font-medium">Définissez vos zones de réception, stockage sec ou réfrigéré pour commencer à indexer vos articles.</p>
                            <button
                                onClick={() => handleOpenAdd(null, 'Zone')}
                                className="px-8 py-3 bg-white dark:bg-slate-800 text-primary font-black rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all"
                            >
                                Commencer la configuration
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-3 bg-slate-50/30 dark:bg-slate-800/20 p-4 rounded-[2rem] border border-slate-100 dark:border-slate-800">
                            {locations.filter(l => !l.parentId).map(loc => (
                                <LocationItem key={loc.id} loc={loc} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer Stats */}
                <div className="p-6 px-8 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                            <span className="size-2 bg-emerald-500 rounded-full"></span>
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Opérationnel</span>
                        </div>
                        <div className="h-4 w-px bg-slate-200 dark:bg-slate-700"></div>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
                            Niveaux : Zone &rsaquo; Allée &rsaquo; Élément &rsaquo; Slot
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-primary">
                        <span className="text-[11px] font-black">{locations.length}</span>
                        <span className="text-[10px] font-black text-slate-400 uppercase">Unités</span>
                    </div>
                </div>
            </div>

            {/* Modal d'ajout/édition Premium */}
            {showAddModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[2100] flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-primary/20 w-full max-w-md border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-500">
                        {/* Sub-modal Premium Header */}
                        <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20">
                            <div className="relative z-10 flex justify-between items-center">
                                <div className="flex gap-4 items-center">
                                    <div className="size-10 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-sm border border-primary/10">
                                        <span className="material-symbols-outlined text-primary text-[20px] font-bold">{editingLocation ? 'edit' : 'add'}</span>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest leading-tight">
                                            {editingLocation ? 'Modifier' : 'Configurer'} {getTypeLabel(targetType)}
                                        </h4>
                                        <p className="text-[9px] text-slate-500 font-bold uppercase tracking-[0.2em] opacity-70">Paramétrage logistique</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowAddModal(false)}
                                    className="size-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm transition-all"
                                >
                                    <span className="material-symbols-outlined text-sm">close</span>
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 space-y-6">
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Nom de l'emplacement</label>
                                <div className="relative group">
                                    <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors text-lg">label</span>
                                    <input
                                        autoFocus
                                        required
                                        className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:border-primary/30 focus:ring-4 focus:ring-primary/5 transition-all text-sm font-black text-slate-900 dark:text-white"
                                        placeholder={targetType === 'Zone' ? 'Ex: Stockage Sec' : targetType === 'Aisle' ? 'Ex: Allée A' : 'Ex: Rack 01'}
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>
                            </div>

                            {(targetType === 'Element' || targetType === 'Slot') && (
                                <div className="animate-in slide-in-from-top-2 duration-300">
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Métadonnées de Capacité</label>
                                    <div className="relative group">
                                        <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors text-lg">info</span>
                                        <input
                                            className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:border-primary/30 focus:ring-4 focus:ring-primary/5 transition-all text-sm font-bold text-slate-900 dark:text-white"
                                            placeholder="Notes ou restrictions de poids..."
                                            value={formData.metadata || ''}
                                            onChange={e => setFormData({ ...formData, metadata: e.target.value })}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="flex-1 py-4 text-[10px] font-black border border-slate-200 dark:border-slate-800 rounded-2xl text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all uppercase tracking-widest"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-4 text-[10px] font-black bg-primary text-white rounded-2xl shadow-xl shadow-primary/20 hover:bg-primary/90 transition-all uppercase tracking-widest transform active:scale-95"
                                >
                                    Enregistrer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocationManager;
